import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Form from "Components/UI/Form/Form";
import schema from "../../ShowCreateActivitie.schema";
import InputTitle from "../InputTitle";
import NewSimpleTextArea from "Components/UI/NewFormGroup/NewSimpleTextArea";
import NewSimpleSelect from "Components/UI/NewFormGroup/NewSimpleSelect";
import NewImgSelect from "Components/UI/NewFormGroup/NewImgSelect";
import NewSimpleDateText from "Components/UI/NewFormGroup/NewSimpleDateText";
import { ITopicDnDResponseOptionalGuid } from "Data/interfaces/Activities/IDragAndDropDataFormat";
import { ICustomOption } from "../../useOptionsSelect";
import { ITopicCreateRequestForm } from "../../useShowCreateActivity";
import { TypeCustomError } from "../../";
import AccessPermission from "Utils/AcessPermission";
import { IUserInforResponse } from "Data/interfaces/Auth/IUserInfoResponse";
import { IStage } from "Data/interfaces/Obra/IObraStages";
import { useFeatureFlag } from "Hooks/useFeatureFlag";
import {
  TypePathUpdateData,
  TypeValueUpdateData,
} from "Data/interfaces/Activities/IUpdateActivityRequest";
import { Body, BodyDefault, BodyDescription, BodyText, MidleHr } from "./styles";

interface IFormShowCreateActivity {
  updateActivity: ITopicDnDResponseOptionalGuid | null;
  deleteLoading?: boolean;
  visibilityOptions: ICustomOption[];
  priorityOptions: ICustomOption[];
  priorityNoBgOptions: ICustomOption[];
  statusOptions: ICustomOption[];
  typeOptions: ICustomOption[];
  stageOptions: string[];
  pointsOptions: string[];
  communicationOptions: string[];
  userOptions: ICustomOption[];
  disciplineOptions: ICustomOption[];
  isSubmit: boolean;
  titleValue: string;
  defaultValues: ITopicCreateRequestForm;
  errors: TypeCustomError[];
  userInfo?: IUserInforResponse;
  stages: IStage[];
  allowOnlyStagesLinkedToDisciplinesInTopics: boolean;
  selectedDisciplines: number[];
  handleTitleValue: (value: string) => void;
  submit: (value: ITopicCreateRequestForm) => void;
  dynamicUpdateSubmit: (path: TypePathUpdateData, values: TypeValueUpdateData) => void;
  handleClearCustomError: (value: TypeCustomError) => void;
  handleCustomError: (value: TypeCustomError) => void;
  onShowCreateStageModal: (value: string) => void;
  onSelectedDisciplines: (value: number[]) => void;
}

const FormShowCreateActivity: FC<IFormShowCreateActivity> = ({
  updateActivity,
  visibilityOptions,
  priorityOptions,
  priorityNoBgOptions,
  statusOptions,
  typeOptions,
  stageOptions,
  pointsOptions,
  communicationOptions,
  userOptions,
  disciplineOptions,
  isSubmit,
  titleValue,
  defaultValues,
  errors,
  userInfo,
  stages,
  allowOnlyStagesLinkedToDisciplinesInTopics,
  selectedDisciplines,
  handleTitleValue,
  submit,
  dynamicUpdateSubmit,
  handleClearCustomError,
  handleCustomError,
  onShowCreateStageModal,
  onSelectedDisciplines,
}) => {
  const { t } = useTranslation('activities');
  const stagesFeatureActivityValidateFlag = useFeatureFlag('stages-feature-activity-validate');
  const stagesFeatureActivityValidate = stagesFeatureActivityValidateFlag.enabled && stagesFeatureActivityValidateFlag.value === 'test';

  const isUpdate = !!updateActivity?.Guid;
  const changeStatusPermission = AccessPermission.statusActivitieEditPermission(
    userInfo,
    updateActivity?.CreationAuthor,
    updateActivity?.TopicAssignedUsers,
    updateActivity?.Visibility
  );
  const editPermission = AccessPermission.createOrEditActivitiePermission(
    userInfo,
    isUpdate,
    updateActivity?.CreationAuthor,
    updateActivity?.TopicAssignedUsers,
    updateActivity?.Visibility
  );
  const createNewOptsPermission = AccessPermission.createNewOptsActivitiePermission();

  const filteredStages = useMemo(() => {
    if (
      allowOnlyStagesLinkedToDisciplinesInTopics &&
      stages.length > 0 &&
      ((defaultValues?.constructionSiteDisciplinesFk &&
        defaultValues.constructionSiteDisciplinesFk?.length > 0) ||
        (selectedDisciplines &&
          selectedDisciplines.length > 0))
    ) {
      return stages.filter((stage) => {
        return stage.IsDefault ||
          stage.ConstructionSiteStageDisciplines.some((discipline) => {
            return defaultValues.constructionSiteDisciplinesFk?.some((disciplineFk) => {
              return discipline.ConstructionSiteDisciplineFk === disciplineFk;
            }) || selectedDisciplines.some((disciplineFk) => {
              return discipline.ConstructionSiteDisciplineFk === disciplineFk;
            });
          })
      });
    }

    return stages;
  }, [
    allowOnlyStagesLinkedToDisciplinesInTopics,
    stages,
    defaultValues?.constructionSiteDisciplinesFk,
    selectedDisciplines,
  ]);

  return (
    <Form
      schema={schema}
      onSubmit={submit}
      isSubmited={isSubmit}
      initialValues={defaultValues}
    >
      <InputTitle
        placeholder={t('form.noTitle')}
        name="title"
        path="Title"
        defaultValue={titleValue}
        onConfirm={handleTitleValue}
        onDynamicUpdateSubmit={dynamicUpdateSubmit}
        errorId="title"
        error={errors.includes('title')}
        tooltipErrorText={t('form.titleRequired')}
        onCustomError={handleCustomError}
        onClearCustomError={handleClearCustomError}
        maxLenght={250}
        isUpdate={isUpdate}
        havePermission={editPermission}
      />
      <MidleHr />
      <Body>
        {isUpdate && (
          <BodyDefault>
            <BodyText>{t('form.statusLabel')}</BodyText>
            <NewSimpleSelect
              className="selectRole"
              placeholder={t('form.statusPlaceholder')}
              options={statusOptions}
              name="statusFk"
              isUpdate={isUpdate}
              path="StatusFk"
              onDynamicUpdateSubmit={dynamicUpdateSubmit}
              havePermission={changeStatusPermission}
            />
          </BodyDefault>
        )}
        <BodyDescription isupdate={`${isUpdate}`}>
          <BodyText isdescription={`${isUpdate}`}>
            {t('form.descriptionLabel')}
          </BodyText>
          <NewSimpleTextArea
            name="description"
            placeholder={t('form.descriptionPlaceholder')}
            maxLenght={2500}
            isUpdate={isUpdate}
            path="Description"
            onDynamicUpdateSubmit={dynamicUpdateSubmit}
            havePermission={editPermission}
          />
        </BodyDescription>
        {!isUpdate && (
          <BodyDefault>
            <BodyText>{t('form.activityTypeLabel')}</BodyText>
            <NewSimpleSelect
              className="selectRole"
              placeholder={t('form.activityPlaceholder')}
              activePlaceholder
              options={typeOptions}
              name="typeFk"
              isUpdate={isUpdate}
              path="TypeFk"
              onDynamicUpdateSubmit={dynamicUpdateSubmit}
              havePermission={editPermission}
            />
          </BodyDefault>
        )}
        <BodyDefault>
          <BodyText>{t('form.responsiblesLabel')}</BodyText>
          <NewImgSelect
            className="selectRole"
            placeholder={t('form.responsiblesPlaceholder')}
            options={userOptions}
            name="assignedUsersFk"
            isUpdate={isUpdate}
            path="AssignedUsersFk"
            onDynamicUpdateSubmit={dynamicUpdateSubmit}
            havePermission={editPermission}
            emptyMsg={t('form.responsiblesEmpty')}
          />
        </BodyDefault>
        <BodyDefault>
          <BodyText iserror={`${errors.includes('discipline')}`}>
            {t('form.disciplinesLabel')}
            {!isUpdate && <BodyText iserror="true">*</BodyText>}
          </BodyText>
          <NewImgSelect
            className="selectRole"
            placeholder={t('form.disciplinesPlaceholder')}
            options={disciplineOptions}
            name="constructionSiteDisciplinesFk"
            errorId="discipline"
            error={errors.includes('discipline')}
            tooltipErrorText={t('form.disciplinesError')}
            onCustomError={handleCustomError}
            onClearCustomError={handleClearCustomError}
            isUpdate={isUpdate}
            path="ConstructionSiteDisciplinesFk"
            onDynamicUpdateSubmit={dynamicUpdateSubmit}
            havePermission={editPermission}
            emptyMsg={t('form.disciplinesEmpty')}
            onValues={(e) => onSelectedDisciplines(e)}
          />
        </BodyDefault>
        <BodyDefault>
          <BodyText iserror={`${errors.includes('date')}`}>
            {t('form.deadlineLabel')}
          </BodyText>
          <NewSimpleDateText
            name="dueDate"
            placeholder={t('form.deadlinePlaceholder')}
            type="date"
            errorId="date"
            error={errors.includes('date')}
            tooltipErrorText={t('form.deadlineError')}
            onCustomError={handleCustomError}
            onClearCustomError={handleClearCustomError}
            isUpdate={isUpdate}
            path="DueDate"
            onDynamicUpdateSubmit={dynamicUpdateSubmit}
            havePermission={editPermission}
            notRequired
          />
        </BodyDefault>
        <BodyDefault>
          <BodyText>{t('form.visibilityLabel')}</BodyText>
          <NewSimpleSelect
            className="selectRole"
            options={visibilityOptions}
            name="visibility"
            isUpdate={isUpdate}
            path="Visibility"
            onDynamicUpdateSubmit={dynamicUpdateSubmit}
            havePermission={editPermission}
          />
        </BodyDefault>
        <BodyDefault>
          <BodyText>{t('form.priorityLabel')}</BodyText>
          <NewSimpleSelect
            className="selectRole"
            placeholder={t('form.priorityPlaceholder')}
            options={priorityOptions}
            optionsCustom={priorityNoBgOptions}
            name="priority"
            isUpdate={isUpdate}
            path="Priority"
            onDynamicUpdateSubmit={dynamicUpdateSubmit}
            havePermission={editPermission}
            notRequired
          />
        </BodyDefault>
        <BodyDefault>
          <BodyText>{t('form.stageLabel')}</BodyText>
          {stagesFeatureActivityValidate && (
            <NewImgSelect
              className="selectRole"
              placeholder={t('form.stagePlaceholder')}
              currentValue={defaultValues.stageFk}
              mode="tags"
              limit={1}
              options={[]}
              tagsDontImg
              optionsTagsDontImg={filteredStages.map((stage) => {
                return stage.Acronym
                  ? `${stage.Acronym} | ${stage.Name}`
                  : `${stage.Name}`;
              }) || []}
              notHasAcronym={filteredStages.filter(stage => !stage.Acronym).map((stage) => {
                return stage.Name;
              })}
              sufix={t('form.stageNewSuffix')}
              name="stageFk"
              error={errors.includes('stage')}
              tooltipErrorText={t('form.stageError')}
              isUpdate={isUpdate}
              path="StageFk"
              onDynamicUpdateSubmit={dynamicUpdateSubmit}
              onModal={onShowCreateStageModal}
              havePermission={editPermission}
              haveCreateOptPermission={createNewOptsPermission}
              emptyMsg={t('form.stageEmpty')}
            />
          )}
          {!stagesFeatureActivityValidate && (
            <NewImgSelect
              className="selectRole"
              placeholder={t('form.stagePlaceholder')}
              currentValue={defaultValues.stageFk}
              mode="tags"
              limit={1}
              options={[]}
              tagsDontImg
              optionsTagsDontImg={stageOptions}
              sufix={t('form.stageNewSuffix')}
              name="stageFk"
              isUpdate={isUpdate}
              path="StageFk"
              onDynamicUpdateSubmit={dynamicUpdateSubmit}
              havePermission={editPermission}
              haveCreateOptPermission={createNewOptsPermission}
              emptyMsg={t('form.stageEmpty')}
            />
          )}
        </BodyDefault>
        <BodyDefault>
          <BodyText iserror={`${errors.includes('point')}`}>
            {t('form.locationLabel')}
            {!isUpdate && <BodyText iserror="true">*</BodyText>}
          </BodyText>
          <NewImgSelect
            className="selectRole"
            placeholder={t('form.locationPlaceholder')}
            currentValue={defaultValues.pointsFk}
            mode="tags"
            options={[]}
            tagsDontImg
            optionsTagsDontImg={pointsOptions}
            sufix={t('form.locationNewSuffix')}
            name="pointsFk"
            errorId="point"
            error={errors.includes('point')}
            tooltipErrorText={t('form.locationError')}
            onCustomError={handleCustomError}
            onClearCustomError={handleClearCustomError}
            havePermission={editPermission}
            haveCreateOptPermission={createNewOptsPermission}
            isUpdate={isUpdate}
            path="PointsFk"
            onDynamicUpdateSubmit={dynamicUpdateSubmit}
            isRequired
            emptyMsg={t('form.locationEmpty')}
          />
        </BodyDefault>
        <BodyDefault>
          <BodyText>{t('form.mediumLabel')}</BodyText>
          <NewImgSelect
            className="selectRole"
            placeholder={t('form.mediumPlaceholder')}
            currentValue={defaultValues.communicationTypeFk}
            mode="tags"
            limit={1}
            options={[]}
            tagsDontImg
            optionsTagsDontImg={communicationOptions}
            sufix={t('form.mediumNewSuffix')}
            name="communicationTypeFk"
            isUpdate={isUpdate}
            path="CommunicationTypeFk"
            onDynamicUpdateSubmit={dynamicUpdateSubmit}
            havePermission={editPermission}
            haveCreateOptPermission={createNewOptsPermission}
            emptyMsg={t('form.mediumEmpty')}
          />
        </BodyDefault>
      </Body>
    </ Form>
  )
}

export default FormShowCreateActivity;
